import { type ComponentType } from '@angular/cdk/portal';
import {
  Component,
  EventEmitter,
  inject,
  Input,
  type OnDestroy,
  Output,
} from '@angular/core';
import { type MatTabChangeEvent } from '@angular/material/tabs';
import { filterUndefined } from '@principle-theorem/shared';
import { type Observable, Subject } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { AuthService } from '../auth.service';
import { AuthSignInErrorHandlerService } from '../auth-sign-in-error-handler.service';
import { type IWorkspaceSelector } from '../workspace-selector/workspace-selector.component';
import { AuthProvider } from '../auth-process.service';
import { Layout, MaterialButtonType } from '../auth-interfaces';

export enum AuthTabIndex {
  SignIn = 0,
  Register = 1,
  PasswordReset = 2,
}

@Component({
  selector: 'pt-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  standalone: false,
  providers: [AuthSignInErrorHandlerService],
})
export class LoginComponent implements OnDestroy {
  private _onDestroy$: Subject<void> = new Subject<void>();
  private _errorHandler = inject(AuthSignInErrorHandlerService);
  auth = inject(AuthService);
  providers: AuthProvider[] = [AuthProvider.Google];
  providersTheme: MaterialButtonType = MaterialButtonType.DEFAULT;
  providersLayout: Layout = Layout.ROW;
  tabIndex: AuthTabIndex = AuthTabIndex.SignIn;
  userIsVerified$: Observable<boolean>;
  @Input() workspaceSelectors: ComponentType<IWorkspaceSelector>[];
  @Output() afterSuccess: EventEmitter<void> = new EventEmitter<void>();
  @Output() afterError: EventEmitter<void> = new EventEmitter<void>();

  constructor() {
    this.userIsVerified$ = this.auth.authUser$.pipe(
      filterUndefined(),
      map((user) => user.emailVerified),
      startWith(false)
    );
  }

  ngOnDestroy(): void {
    this._onDestroy$.next();
  }

  async onSuccess(): Promise<void> {
    await this.auth.updateClaims();
    this.afterSuccess.emit();
  }

  async onError(event: Error): Promise<void> {
    const result = await this._errorHandler.handle(event);

    if (result === 'handled') {
      return;
    }

    this.afterError.emit();
  }

  updateSelectedTab($event: MatTabChangeEvent): void {
    this.tabIndex = $event.index;
  }

  trackByProvider(_index: number, item: AuthProvider): string {
    return item;
  }
}
